import React, { useMemo, useState } from 'react';
import { View, StyleSheet, TextInput, Pressable } from 'react-native';
import { useLocalSearchParams } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import PublicPage from '@/components/Pages/PublicPage';
import MetaHead from '@/components/MetaHead';
import SubmitButton from '@/components/Buttons/SubmitButton';
import Text from '@/components/Typography/Text';
import { sitesApi, SITE_REPORT_REASONS } from '@/lib/api';
import { SITES_DOMAIN, siteNameFromInput } from '@/lib/publishedSites';

export default function ReportSitePage() {
  const { colors } = useTheme();
  const params = useLocalSearchParams();
  const [site, setSite] = useState(String(params.site || ''));
  const [reason, setReason] = useState(null);
  const [details, setDetails] = useState('');
  const [state, setState] = useState({ submitting: false, done: false, error: null });
  const name = useMemo(() => siteNameFromInput(site), [site]);
  const inputStyle = [styles.input, { borderColor: colors.border_color_secondary, color: colors.text_color_primary }];

  const submit = async () => {
    if (!name || !reason) return;
    setState({ submitting: true, done: false, error: null });
    try {
      await sitesApi.report({ name, reason, details });
      setState({ submitting: false, done: true, error: null });
    } catch (error) {
      setState({ submitting: false, done: false, error: error?.message || 'Could not send the report. Try again.' });
    }
  };

  return (
    <PublicPage title="Report a site" subtitle={`Tell us about a page published on ${SITES_DOMAIN} that breaks our rules.`}>
      <MetaHead title="Report a site" description="Report a page published with Gab AI." noindex />
      {state.done ? (
        <View style={styles.form}>
          <Text size="md" weight="semibold">Thanks — we received your report.</Text>
          <Text size="sm" color="secondary">Our team reviews reports and removes sites that break the rules.</Text>
        </View>
      ) : (
        <View style={styles.form}>
          <Text size="sm" weight="medium">Site</Text>
          <TextInput
            value={site}
            onChangeText={setSite}
            placeholder={`name.${SITES_DOMAIN}`}
            placeholderTextColor={colors.text_color_tertiary}
            autoCapitalize="none"
            autoCorrect={false}
            style={inputStyle}
            accessibilityLabel="Site address"
          />
          {site && !name ? <Text size="xs" color="secondary">Enter the address shown in the browser, like name.{SITES_DOMAIN}.</Text> : null}
          <Text size="sm" weight="medium">What’s wrong?</Text>
          <View style={styles.reasons} accessibilityRole="radiogroup">
            {SITE_REPORT_REASONS.map((r) => {
              const selected = reason === r.value;
              return (
                <Pressable
                  key={r.value}
                  onPress={() => setReason(r.value)}
                  accessibilityRole="radio"
                  accessibilityState={{ checked: selected }}
                  style={[styles.reason, { borderColor: selected ? colors.text_color_primary : colors.border_color_secondary, backgroundColor: selected ? colors.solid_color_secondary : 'transparent' }]}
                >
                  <Text size="sm" weight={selected ? 'semibold' : 'regular'}>{r.label}</Text>
                </Pressable>
              );
            })}
          </View>
          <Text size="sm" weight="medium">Details (optional)</Text>
          <TextInput
            value={details}
            onChangeText={setDetails}
            multiline
            maxLength={2000}
            placeholder="What did you see?"
            placeholderTextColor={colors.text_color_tertiary}
            style={[inputStyle, styles.details]}
            accessibilityLabel="Details"
          />
          {state.error ? <Text size="sm" color="secondary">{state.error}</Text> : null}
          <SubmitButton onPress={submit} isLoading={state.submitting} isDisabled={!name || !reason} title={state.submitting ? 'Sending…' : 'Send report'} />
        </View>
      )}
    </PublicPage>
  );
}

const styles = StyleSheet.create({
  form: { width: '100%', gap: 10 },
  input: { borderWidth: 1, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 10, fontSize: 15 },
  details: { minHeight: 96, textAlignVertical: 'top' },
  reasons: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  reason: { borderWidth: 1, borderRadius: 16, paddingHorizontal: 12, paddingVertical: 6 },
});
